import React, { useEffect, useMemo } from 'react';
import { Portal } from '../../../components/portal/Portal';
import { useCartContext } from '../context/CartContext';
import DomFont from '@jbhi-fi/lanyard-ui/font/DomFont';
import { styles } from './FrequentlyBoughtTogether.css';
import { AlgoliaProductHit } from '../../types';
import { ProductCard } from '@jbhi-fi/components/src/ProductCard';
import { useMinicartUiContext } from '../context/MinicartUiContext';
import { AlgoliaHitTransformer } from '@jbhi-fi/components/src/Algolia/transformers/AlgoliaHitTransformer';
import { useProductActionsContext } from '@jbhi-fi/components/src/context/ProductActionsProvider';
import { useProductCompare } from '@jbhi-fi/components/src/ProductCompare';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag/useFeatureFlag';
import { getStoreCurrency } from '../../shopify/getStoreCurrency';
import { useProducts } from '@jbhi-fi/components/src/hooks/useProducts/useProducts';
import { useSuggestedProducts } from '@jbhi-fi/components/src/hooks/useSuggestedProducts';
import { ProductCarousel } from '@jbhi-fi/components/src/ProductCarousel/ProductCarousel';
import { getCartLoading, setCartLoading } from '../../legacyCart';
import { IsAttachedService } from '../../../templates/components/pdp/utils/product-service';

type FrequentlyBoughtTogetherProductsCarouselProps = {
  product: { sku: string };
  excludedSkus?: Array<string>;
  googleRecIndex: string;
};

export type GoogleRecIndexType = {
  googleRecIndex?: string;
};

const IS_CART_PAGE = window.location.pathname.startsWith('/cart');

export const FrequentlyBoughtTogetherCarousel = ({
  indexType,
  products,
}: {
  indexType: string;
  products: AlgoliaProductHit[] | undefined;
}) => {
  const { cart } = useCartContext();
  const cartType = IS_CART_PAGE ? 'cart' : 'minicart';
  const { isOpen: isMinicartOpen } = useMinicartUiContext();
  const { onAddToCart, productCartPropsOverrides = {} } = useProductActionsContext();
  const { isAddedToCompare, compareHandleClick, shouldShowCompare } = useProductCompare(false);
  const { isLoggedIn } = useCustomerContext();

  const filteredProducts = useMemo(
    () => products?.filter((item: AlgoliaProductHit) => !cart.items.some((cartItem: any) => cartItem.sku === item.sku)),
    [products, cart?.items]
  );

  useEffect(() => {
    if (!filteredProducts?.length) {
      return;
    }

    const productImpressions =
      isMinicartOpen || IS_CART_PAGE
        ? filteredProducts?.map((hit, i) => ({
            ...hit.google_tag_manager,
            list: `recommendations-${cartType}-${indexType}`,
            position: i,
          }))
        : null;

    if (productImpressions?.length) {
      window.gtm.track('productImpression', {
        products: productImpressions,
      });
    }
  }, [filteredProducts, isMinicartOpen, cartType]);

  return filteredProducts?.length ? (
    <>
      <DomFont className={styles.heading}>Get it all together</DomFont>
      <ProductCarousel
        fullBleed={false}
        fullHeightItems
        products={filteredProducts}
        maxNumProducts={5}
        renderProduct={({ hit }) => (
          <ProductCard
            {...AlgoliaHitTransformer.toProductCardProps(hit, {
              trackingDescriptor: {
                page: cartType,
                group: 'googleRelated',
                category: 'frequentlyBoughtTogether',
                currency: getStoreCurrency(),
                ecommerceExtraProps: {
                  list: `recommendations-${cartType}-${indexType}`,
                },
              },
            })}
            {...productCartPropsOverrides}
            variant="small"
            shouldHideWishlist
            shouldHideCartOverlay
            key={hit.objectID}
            getIsCompared={isAddedToCompare}
            onCompare={compareHandleClick}
            getShouldShowCompare={shouldShowCompare}
            isLoggedIn={isLoggedIn}
            onAddToCart={
              onAddToCart
                ? (e, _sku, variantId, eventData) => onAddToCart(e, hit, variantId, eventData?.trackingDescriptor)
                : undefined
            }
          />
        )}
      />
    </>
  ) : null;
};

const FrequentlyBoughtTogetherProductsCarouselWrapper = ({
  product,
  googleRecIndex,
  excludedSkus,
}: FrequentlyBoughtTogetherProductsCarouselProps) => {
  const isFBTMiniCartEnabled = useFeatureFlag('isFrequentlyBoughtTogetherMiniCartEnabled');
  const isEnhancedGoogleRecommendationsEnabled = useFeatureFlag('isEnhancedGoogleRecommendationsEnabled');

  const smartModuleSettings = window?.smartModuleSettings?.cartPage;

  const { products = [] } = useProducts({
    productSkus: [product.sku],
    options: { shouldResolveExtraOnlineProductFields: true },
  });

  const { suggestions, isLoading: isSuggestedProductsLoading } = useSuggestedProducts({
    products,
    googleRecNumProducts: smartModuleSettings?.productCount || 12,
    googleRecModel: smartModuleSettings.googleRecModelType || 'FBT',
    isAttachSuggestionEnabled: false,
    googleRecAcceptedDeviationPercentage: window?.smartModuleSettings?.googleAcceptedDeviationPercentage,
    excludedSkus,
    shouldEnhanceSuggestions: !!isEnhancedGoogleRecommendationsEnabled,
  });

  useEffect(() => {
    if (isSuggestedProductsLoading !== getCartLoading()) {
      setCartLoading(isSuggestedProductsLoading);
    }
  }, [isSuggestedProductsLoading]);

  if (
    (IS_CART_PAGE && !suggestions?.length && !isSuggestedProductsLoading) ||
    (isFBTMiniCartEnabled && (!suggestions?.length || IS_CART_PAGE))
  ) {
    return null;
  }
  return <FrequentlyBoughtTogetherCarousel products={suggestions} indexType={googleRecIndex} />;
};

export const FrequentlyBoughtTogether = ({ googleRecIndex }: GoogleRecIndexType) => {
  const { cart } = useCartContext();
  const filteredCartItems = cart?.items.filter(
    item => !IsAttachedService(item) && !item.isGiftCard && !item.properties._marketplaceSellerId
  );
  const maxLineItems = useFeatureFlag('miniCartFBTMaxLineItems') ?? filteredCartItems?.length;
  const lineItems = IS_CART_PAGE ? filteredCartItems : filteredCartItems?.slice(0, maxLineItems);

  return (
    <>
      {lineItems?.map((lineItem, i) => {
        const container = document.getElementById(`fbt-${lineItem.line_id}`);
        const hasExtraSuggestions = document.getElementById(`bundle-${lineItem.line_id}`);
        if (!container || hasExtraSuggestions) return null;

        return (
          <Portal container={container} key={i}>
            <FrequentlyBoughtTogetherProductsCarouselWrapper
              product={lineItem}
              googleRecIndex={googleRecIndex ?? 'FBT'}
              excludedSkus={lineItems.map(item => item.sku)}
            />
          </Portal>
        );
      })}
    </>
  );
};
